// pwa/src/components/Products.js
import React, { useContext, useEffect, useState, useRef } from "react";
import { AppContext } from "../utilities/context";
import { FormattedMessage, useIntl } from "react-intl";
import axios from "axios";
import { DataGrid, GridActionsCellItem, GridRowId } from "@mui/x-data-grid";
import Container from "@mui/material/Container";
import Layout from "./Layout";
import ProductModalContent from "./ProductModalContent";
import Grid from "@mui/material/Grid";
// import DOMPurify from 'dompurify';
import { Button, Box } from "@mui/material";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";

const Products = () => {
  const intl = useIntl();
  const context = useContext(AppContext);
  const [products, setProducts] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [syncState, setSyncState] = useState({
    isLoading: false,
    isSyncing: false,
    progress: 0,
    status: "none",
    error: null,
  });
  const ProductModalContentRef = useRef();
  // Nuevo estado para el ID del producto que se está editando
  const [productToEditId, setProductToEditId] = useState(null);

  const handleOpenModalContent = () => {
    setProductToEditId(null); // Asegurarse de que no haya ID de producto para edición
    ProductModalContentRef.current?.handleClickOpen();
  };

  const checkSyncStatus = async () => {
    try {
      const response = await axios.get(
        "/api/marketplace/tiendanube/backgroundtask/status"
      );
      const prevTask = response.data;
      const newState = {
        ...syncState,
        isSyncing: ["pending", "processing"].includes(prevTask.status),
        isLoading: ["pending", "processing"].includes(prevTask.status),
        progress: prevTask.progress || 0,
        status: prevTask.status,
        processedItems: prevTask.processedItems,
        totalItems: prevTask.totalItems,
      };

      setSyncState(newState);

      // Si la sincronización terminó, actualizar los productos
      if (!["pending", "processing"].includes(prevTask.status)) {
        getProducts();
      }
    } catch (error) {
      console.error("Error al verificar estado:", error);
      setSyncState((prev) => ({
        ...prev,
        isSyncing: false,
        isLoading: false,
        status: "error",
        error: error.message,
      }));
    }
  };

  const getProducts = () => {
    const { page, pageSize } = paginationModel;

    let sortParams = "";
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      sortParams = `&sortField=${field}&sortOrder=${sort}`;
    }

    axios
      .get("/api/products?page=" + page + "&pageSize=" + pageSize + sortParams)
      .then((response) => {
        setProducts(response.data.products);
        setTotalRows(response.data.total);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const syncProducts = () => {
    // 1. Resetear el estado y marcar como sincronizando
    setSyncState({
      isLoading: true,
      isSyncing: true,
      progress: 0,
      status: "pending",
      error: null,
      processedItems: 0,
      totalItems: 0,
    });
    axios
      .get("/api/marketplace/tiendanube/syncproducts")
      .then((response) => {
        context.updateAlertState([
          {
            open: true,
            message: "Sincro iniciada",
            severity: "success",
          },
        ]);
      })
      .catch((error) => {
        let errorMessage = error.message;
        if (error.response && error.response.status === 409) {
          errorMessage = intl.formatMessage({
            id: "app.products.sync.conflict",
          });
        }

        setSyncState((prev) => ({
          ...prev,
          isSyncing: false,
          isLoading: false,
          status: "error",
          error: errorMessage,
        }));
        context.updateAlertState([
          {
            open: true,
            message: errorMessage,
            severity: "error",
          },
        ]);
      });
    // 3. Iniciar inmediatamente la verificación (no esperar al siguiente intervalo)
    checkSyncStatus();
  };

  const handleEditClick = (id) => {
    setProductToEditId(id); // Establecer el ID del producto a editar
    ProductModalContentRef.current?.handleClickOpen();
    console.log("Edit clicked for row:", id);
  };

  const handleDeleteClick = (id) => {
    console.log("Delete clicked for row:", id);
  };

    // Callback para manejar la recarga de la lista después de guardar en el modal
  const handleModalSaveSuccess = () => {
    getProducts();
  };

  useEffect(() => {
    getProducts();
    checkSyncStatus();
    if (context) {
      context.updateBreadcrumbsState([
        {
          label: intl.formatMessage({ id: "app.header.home.label" }),
          url: "/",
        },
        {
          label: intl.formatMessage({ id: "app.header.shop.products.label" }),
        },
      ]);
    }
  }, [paginationModel, sortModel]); // Se ejecuta cuando cambia la paginación o la ordenación

  useEffect(() => {
    let interval;
    if (syncState.status === "pending" || syncState.status === "processing") {
      // Ejecutar inmediatamente y luego cada 3 segundos
      interval = setInterval(checkSyncStatus, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [syncState.status]); // Ahora observamos syncState.status en lugar de isSyncing

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "name", headerName: "Name", width: 150 },
    { field: "barCode", headerName: "BarCode", width: 120 },
    { field: "sku", headerName: "SKU", width: 100 },
    {
      field: "categoryDetails",
      headerName: "Categoría",
      width: 120,
      valueGetter: (value) => value?.name || "",
    },
    { field: "description", headerName: "Description", flex: 1 },
    { field: "url", headerName: "Link", width: 150 },
    { field: "price", headerName: "Price", width: 100, type: "number" },
    {
      field: "tags",
      headerName: "Tags",
      width: 150,
      valueGetter: (value) => value?.map(t => t.name).join(", ") || "",
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={() => handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },

    // {
    //   field: "name",
    //   headerName: "Nombre",
    //   width: 200,
    //   valueGetter: (params) => {
    //     return params?.es || "";
    //   },
    // },
    // {
    //   field: "description",
    //   headerName: "Description",
    //   valueGetter: (params) => {
    //     return params?.es || "";
    //   },
    //   flex: 1, // Esta columna ocupará el espacio disponible
    //   // renderCell: (params) => {
    //   //   console.log("renderCell params:", params);
    //   //   return (<div dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(params.value?.es || '')}} />)
    //   // },
    // },
    // { field: "price", headerName: "Precio", width: 150, type: "number" },
  ];

  return (
    <Layout>
      <Container maxWidth={false} disableGutters>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={1}></Grid>
          <Grid item xs={12} sm={10} sx={{ textAlign: "right" }}>
            {/* <Button
              variant="contained"
              size="small"
              onClick={syncProducts}
              sx={{ mr: 1 }}
              disabled={syncState.isLoading || syncState.isSyncing}
            >
              {syncState.isLoading || syncState.isSyncing
                ? "Working..."
                : "Sync Products"}
            </Button> */}
            <Button
              variant="contained"
              size="small"
              onClick={handleOpenModalContent}
              sx={{ mr: 1 }}
            >
              <FormattedMessage
                id={"app.content.create.label"}
                defaultMessage={
                  "Crear"
                }
              />
            </Button>
          </Grid>

          <Grid item xs={12} sm={1}></Grid>
          <Grid item xs={12} sm={1}></Grid>
          <Grid item xs={12} sm={10} sx={{ textAlign: "center" }}>
            <Box sx={{ height: 400, width: "100%" }}>
              <DataGrid
                rows={products}
                columns={columns}
                pagination
                paginationMode="server"
                sortingMode="server"
                rowCount={+totalRows}
                pageSizeOptions={[5, 10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sortModel={sortModel}
                onSortModelChange={(model) => setSortModel(model)}
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={1}></Grid>
        </Grid>
        {/* PASAR EL ID DEL PRODUCTO A EDITAR AL MODAL, shopId sigue siendo 1 */}
        <ProductModalContent shopId={context.user.id || null} productId={productToEditId} ref={ProductModalContentRef} onSaveSuccess={handleModalSaveSuccess} />
      </Container>
    </Layout>
  );
};

export default Products;
